'use client'

import { useRef, useEffect, useCallback, useState } from 'react'
import type { CharacterMatch, HanziLookupStatic } from '@/types/hanzi-lookup'

interface HandwritingCanvasProps {
  onPredictions: (predictions: string[]) => void
  width?: number
  height?: number
  className?: string
}

type Point = [number, number]
type Stroke = Point[]

export function HandwritingCanvas({
  onPredictions,
  width = 256,
  height = 256,
  className
}: HandwritingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [currentStroke, setCurrentStroke] = useState<Stroke>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const matcherRef = useRef<InstanceType<HanziLookupStatic['Matcher']> | null>(
    null
  )

  // Load HanziLookup library
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.HanziLookup?.data?.['mmah']) {
      matcherRef.current = new window.HanziLookup.Matcher('mmah')
      setIsInitialized(true)
      return
    }

    const script = document.createElement('script')
    script.src = '/lib/hanzilookup.min.js'
    script.async = true
    script.onload = () => {
      window.HanziLookup.init('mmah', '/lib/mmah.json', (success) => {
        if (success) {
          matcherRef.current = new window.HanziLookup.Matcher('mmah')
          setIsInitialized(true)
        }
      })
    }
    document.body.appendChild(script)

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  // Get canvas coordinates from event
  const getPoint = useCallback(
    (e: React.MouseEvent | React.TouchEvent): Point => {
      const canvas = canvasRef.current
      if (!canvas) return [0, 0]
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      if ('touches' in e) {
        const touch = e.touches[0]
        return [
          (touch.clientX - rect.left) * scaleX,
          (touch.clientY - rect.top) * scaleY
        ]
      }
      return [(e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY]
    },
    []
  )

  // Redraw canvas
  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    // Detect dark mode from document class
    const isDark = document.documentElement.classList.contains('dark')

    // Theme-aware colors
    const bgColor = isDark ? '#1f2937' : '#ffffff'
    const strokeColor = isDark ? '#f3f4f6' : '#1f2937'
    const guideColor = isDark ? '#374151' : '#e5e7eb'

    // Fill canvas with background
    ctx.fillStyle = bgColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Draw guide lines
    ctx.strokeStyle = guideColor
    ctx.lineWidth = 0.5
    ctx.setLineDash([4, 4])

    // Cross
    ctx.beginPath()
    ctx.moveTo(canvas.width / 2, 0)
    ctx.lineTo(canvas.width / 2, canvas.height)
    ctx.moveTo(0, canvas.height / 2)
    ctx.lineTo(canvas.width, canvas.height / 2)
    ctx.stroke()

    // Diagonals
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(canvas.width, canvas.height)
    ctx.moveTo(canvas.width, 0)
    ctx.lineTo(0, canvas.height)
    ctx.stroke()

    ctx.setLineDash([])

    // Draw strokes
    const allStrokes = [...strokes, currentStroke].filter((s) => s.length > 0)
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = 4
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    for (const stroke of allStrokes) {
      if (stroke.length < 2) continue
      ctx.beginPath()
      ctx.moveTo(stroke[0][0], stroke[0][1])
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i][0], stroke[i][1])
      }
      ctx.stroke()
    }
  }, [strokes, currentStroke])

  useEffect(() => {
    redraw()
  }, [redraw])

  // Recognize character
  const recognize = useCallback(
    (allStrokes: Stroke[]) => {
      if (!isInitialized || !matcherRef.current || allStrokes.length === 0) {
        onPredictions([])
        return
      }

      const analyzed = new window.HanziLookup.AnalyzedCharacter(allStrokes)
      matcherRef.current.match(analyzed, 8, (matches: CharacterMatch[]) => {
        onPredictions(matches.map((m) => m.character))
      })
    },
    [isInitialized, onPredictions]
  )

  // Start stroke
  const handleStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault()
      const point = getPoint(e)
      setIsDrawing(true)
      setCurrentStroke([point])
    },
    [getPoint]
  )

  // Continue stroke
  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return
      e.preventDefault()
      const point = getPoint(e)
      setCurrentStroke((prev) => [...prev, point])
    },
    [isDrawing, getPoint]
  )

  // End stroke
  const handleEnd = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return
      e.preventDefault()
      setIsDrawing(false)

      if (currentStroke.length > 1) {
        const newStrokes = [...strokes, currentStroke]
        setStrokes(newStrokes)
        recognize(newStrokes)
      }
      setCurrentStroke([])
    },
    [isDrawing, currentStroke, strokes, recognize]
  )

  // Clear canvas
  const clear = useCallback(() => {
    setStrokes([])
    setCurrentStroke([])
    onPredictions([])
  }, [onPredictions])

  // Undo last stroke
  const undo = useCallback(() => {
    if (strokes.length === 0) return
    const newStrokes = strokes.slice(0, -1)
    setStrokes(newStrokes)
    recognize(newStrokes)
  }, [strokes, recognize])

  return {
    canvas: (
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={className}
        style={{ touchAction: 'none' }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />
    ),
    clear,
    undo,
    strokeCount: strokes.length,
    isInitialized
  }
}
